
      import { createComponent } from '@lit/react'
      import { type EventName } from '@lit/react'
      import * as React from 'react'

      import Component from '../../components/copybox-input/copybox-input.component.js'
      import type { DdsCopyEvent } from '../../events/events'
import type { DdsChangeEvent } from '../../events/events'
import type { DdsInputEvent } from '../../events/events'
import type { DdsKeydownEvent } from '../../events/events'
import type { DdsBlurEvent } from '../../events/events'
import type { DdsFocusEvent } from '../../events/events'
import type { DdsNotAllowedEvent } from '../../events/events'
      export type { DdsCopyEvent } from '../../events/events'
export type { DdsChangeEvent } from '../../events/events'
export type { DdsInputEvent } from '../../events/events'
export type { DdsKeydownEvent } from '../../events/events'
export type { DdsBlurEvent } from '../../events/events'
export type { DdsFocusEvent } from '../../events/events'
export type { DdsNotAllowedEvent } from '../../events/events'

      const tagName = 'dap-ds-copybox-input'

      /**
 * `dap-ds-copybox-input`
 * @summary A copybox input is a field for copying an input value.
 * @element dap-ds-copybox-input
 * @title - CopyBox input
 *
 * @property {string} label - The label of the input.
 * @property {string} placeholder - The placeholder of the input.
 * @property {string} description - The description of the input.
 * @property {string} tooltip - The tooltip of the input.
 * @property {'top' | 'right' | 'bottom' | 'left'} tooltipPlacement - The position of the tooltip. Can be `top`, `right`, `bottom`, or `left`.
 * @property {'success' | 'error'} status - The status of the input. Can be `success` or `error`.
 * @property {'xs' | 'sm' | 'sm'} size - The size of the input. Default is `sm`.
 * @property {string} name - The name of the input.
 * @property {string} value - The value of the input.
 * @property {boolean} disabled - The disabled state of the input. Default is false.
 * @property {boolean} required - The required state of the input. Default is false.
 * @property {boolean} readonly - The readonly state of the input. Default is false.
 * @property {boolean} autofocus - The autofocus state of the input. Default is false.
 * @property {string} feedback - The feedback of the input.
 * @property {'negative' | 'positive' | 'warning'} feedbackType - The feedback type of the input. Can be `negative`, `positive`, or `warning`.
 * @property {boolean} loading - The loading state of the input. Default is false.
 * @property {boolean} optional - The optional state of the input.
 * @property {string} optionalLabel - The optional label of the input.
 * @property {boolean} subtle - The weight of the label. Default is `false`
 * @property {string} copyButtonAriaLabel - The aria label of the copy button.
 *
 * @event {{ value: string }} dds-copy - Fired when the user clicks on the copy button.
 * @event {{ value: string }} dds-change - Fired when the input value changes.
 * @event {{ value: string }} dds-input - Fired when the input value changes.
 * @event {{ value: string, originalEvent: Event }} dds-keydown - Fired when a key is pressed down.
 * @event {{ void }} dds-blur - Fired when the input loses focus.
 * @event {{ void }} dds-focus - Emitted when the input gains focus.
 * @event {{ void }} dds-not-allowed - Emitted when the value cannot be copied.
 *
 * @cssproperty --dds-input-height - The height of the input. Default is var(--dds-spacing-1000).
 * @cssproperty --dds-input-padding - The padding of the input. Default is var(--dds-spacing-300).
 * @cssproperty --dds-input-font-size - The font size of the input. Default is var(--dds-font-base).
 * @cssproperty --dds-input-border-color - The border color of the input. Default is var(--dds-border-neutral-base).
 * @cssproperty --dds-input-background-color - The background color of the input. Default is var(--dds-fields-background-default).
 * @cssproperty --dds-input-text-color - The text color of the input. Default is var(--dds-text-neutral-base).
 * @cssproperty --dds-input-border-radius - The border radius of the input. Default is var(--dds-radius-base).
 * @cssproperty --dds-input-border-width - The border width of the input. Default is var(--dds-border-width-base).
 * @cssproperty --dds-input-disabled-background - The background color of the disabled input. Default is var(--dds-fields-background-disabled).
 * @cssproperty --dds-input-disabled-text - The text color of the disabled input. Default is var(--dds-text-neutral-disabled).
 * @cssproperty --dds-input-readonly-background - The background color of the readonly input. Default is var(--dds-fields-background-read-only).
 * @cssproperty --dds-input-readonly-text - The text color of the readonly input. Default is var(--dds-text-neutral-subtle).
 * @cssproperty --dds-input-success-border - The border color of the success input. Default is var(--dds-border-positive-base).
 * @cssproperty --dds-input-error-border - The border color of the error input. Default is var(--dds-border-negative-base).
 * @cssproperty --dds-input-addon-background - The background color of the input addon. Default is var(--dds-background-ui-neutral-enabled).
 * @cssproperty --dds-input-addon-success-background - The background color of the success input addon. Default is var(--dds-background-ui-positive-enabled).
 * @cssproperty --dds-input-addon-error-background - The background color of the error input addon. Default is var(--dds-background-ui-negative-enabled).
 *
 * @csspart base - The base container of the component.
 * @csspart postfix - The inner postfix icon part of the component.
 * @csspart button - The button of the component.
 */
      const reactWrapper = createComponent({
        tagName,
        elementClass: Component,
        react: React,
        events: {
          onDdsCopy: 'dds-copy' as EventName<DdsCopyEvent>,
onDdsChange: 'dds-change' as EventName<DdsChangeEvent>,
onDdsInput: 'dds-input' as EventName<DdsInputEvent>,
onDdsKeydown: 'dds-keydown' as EventName<DdsKeydownEvent>,
onDdsBlur: 'dds-blur' as EventName<DdsBlurEvent>,
onDdsFocus: 'dds-focus' as EventName<DdsFocusEvent>,
onDdsNotAllowed: 'dds-not-allowed' as EventName<DdsNotAllowedEvent>
        },
        displayName: "DapDSCopyBoxInput"
      })

      export default reactWrapper
    